import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import InputSection from 'infrastructure/js/components/Dialog/InputSection/inputSection';
import TextField from 'infrastructure/js/components/controls/TextField/textField';
import Validation from 'infrastructure/js/components/controls/controlsValidations.js';
import useLabels from 'infrastructure/js/hooks/useLabels';
import List from 'infrastructure/js/components/List/list';
import Combobox from 'infrastructure/js/components/controls/Combobox/combobox.js';
import {enumTypes, getEnumValue} from '../../../../../../utils/enumHelper';
import Checkbox from 'infrastructure/js/components/controls/Checkbox/checkbox';
import Button from 'infrastructure/js/components/controls/Button/button';
import Normalize from 'infrastructure/js/components/controls/controlsNormalizations';
import comparisonOperatorsEnum from '../../../../../../enums/comparisonOperators';
import {
  attachmentTypeOptions,
  frequencyOptions, frequencyTypesOptions, QPL_MAX_DECIMAL, QPL_MIN_DECIMAL,
  qplTestPassCriteriaOptions
} from '../../../utils/qplHelper';
import RangeListItem, { checkConflict } from '../../../../../Common/Controls/AddRemoveList/AddRemoveListItem/RangeListItem/rangeListItem';
import PermissionManager from 'infrastructure/js/utils/permissionManager';
import {unitTypes} from 'infrastructure/js/utils/uomHelper';
import withPortal from 'infrastructure/js/components/HOCs/withPortal/withPortal';
import { PL_NoFormComboBox } from 'infrastructure/js/components/controls/Combobox/combobox.js';

import './qplTestListItem.scss';


const QPL_MINIMUM_VALUE_FIELD_NAME = 'minimumValue';
const QPL_MAXIMUM_VALUE_FIELD_NAME = 'maximumValue';
const QPL_MINIMUM_UNIT_FIELD_NAME = 'minimumUnit';
const QPL_MAXIMUM_UNIT_FIELD_NAME = 'maximumUnit';

function getIndexFromPath(path, fieldName) {
  const match = path.match(new RegExp(`\\[${fieldName}\\]\\[(\\d+)\\]`));
  return match ? parseInt(match[1], 10) : null;
}

function isShowPerMaterialQuantity(testFields, index) {
  const found =  testFields?.[index]?.find(item => (item.type === 'FREQUENCY' && item.showPerMaterialQuantity === true))
  return !!found;
}

function isRemovable(item) {
  return item && !item.default;
}

function getMaxLength(item) {
  if (item?.type === 'TEXT') {
    return item.name === QPL_MINIMUM_UNIT_FIELD_NAME || item.name === QPL_MAXIMUM_UNIT_FIELD_NAME ? 10 : 100;
  }
  return undefined; //no limitation
}

function getNormalize(item) {
  if (item.type === 'DECIMAL') {
    return Normalize.normalizeFloat(QPL_MIN_DECIMAL, QPL_MAX_DECIMAL, 5);
  }
  if (item.type === 'INTEGER') {
    const min = item.name === 'numOfRepetitions' ? 1 : 0;
    return Normalize.number(true, min, Number.MAX_SAFE_INTEGER);
  }
  return undefined;
}


const ComboboxPortal = withPortal(Combobox);

const NoFormComboboxPortal = withPortal(PL_NoFormComboBox);



const QplTestListItem = ({ name,
                           testFields = [],
                           optionalFields = [],
                           index,
                           listName,
                           change,
                           onFieldAdd = null,
                           onFieldRemove = null,
                           onFieldChange = null,
                           fieldData,
                           isViewOnly = false,
                           testType = '',

}) => {

  const labels = useLabels('mat.forms.qplTestForm.quantityTestListItem.');
  const allLabels = useLabels('');
  const validationLabels = useLabels('mat.validation.messages.');

  const [showOptionalFieldsMenu, setShowOptionalFieldsMenu] = useState(false);
  const [frequencyUpdateKey, setFrequencyUpdateKey] = useState(0);
  const [minValue, setMinValue] = useState(null);
  const [maxValue, setMaxValue] = useState(null);

  const minFieldValue = fieldData?.[QPL_MINIMUM_VALUE_FIELD_NAME];
  const maxFieldValue = fieldData?.[QPL_MAXIMUM_VALUE_FIELD_NAME];


  useEffect(() => {
    setMinValue(minFieldValue || '');
    setMaxValue(maxFieldValue || '');
  }, [minFieldValue, maxFieldValue]);

  const majorUOMType = PermissionManager.isWeightSupported() ? unitTypes.WEIGHT : unitTypes.LENGTH;


  const validateFrequencyRangeConflict = useCallback((value, allValues, props, name) => {

    const rowIndex = getIndexFromPath(name, 'frequency');
    if (rowIndex === null) {
      return;
    }
    const currentRow = allValues?.[listName]?.[index]?.frequency?.[rowIndex];
    if (!currentRow?.operator?.value || value === undefined || value === null) {
      return;
    }

    const { operator, minQuantity, quantity } = currentRow;
    const minQuantityVal = parseInt(minQuantity);
    const quantityVal = parseInt(quantity);

    if(minQuantityVal > quantityVal) {
      return validationLabels.get('minGreaterThanMax');
    }

    let error;

    allValues?.[listName]?.[index]?.frequency?.forEach((otherRow, otherIndex) => {
      if (rowIndex !== otherIndex  )  {
        const { operator: otherOperator, minQuantity: otherMinQuantity, quantity: otherQuantity } = otherRow;
        const otherMinQuantityVal = parseInt(otherMinQuantity);
        const otherQuantityVal = parseInt(otherQuantity);

        if (!otherOperator?.value || isNaN(otherQuantityVal) || (otherOperator?.value === comparisonOperatorsEnum.BETWEEN && isNaN(otherMinQuantityVal))) {
          return; // Skip invalid rows
        }

        const conflict = checkConflict(
            { operator: operator?.value, minQuantity: minQuantityVal, quantity: quantityVal },
            { operator: otherOperator?.value, minQuantity: otherMinQuantityVal, quantity: otherQuantityVal }
        );

        if (conflict) {
          error = validationLabels.get('rangeConflict');
        }
      }
    });

    return error;
  }, [ index, listName, validationLabels]);

  const isMandatoryItem = (item= {}) => {
    if (item.name === QPL_MINIMUM_VALUE_FIELD_NAME) {
      return !maxValue;
    }
    if (item.name === QPL_MAXIMUM_VALUE_FIELD_NAME) {
      return !minValue;
    }
    if (item.name === QPL_MINIMUM_UNIT_FIELD_NAME) {
      return !maxValue || !!minValue;
    }
    if (item.name === QPL_MAXIMUM_UNIT_FIELD_NAME) {
      return !minValue || !!maxValue;
    }

    return item.mandatory;
  }

  const isDisabledItem = (item= {}) => {
    if (item.name === QPL_MINIMUM_UNIT_FIELD_NAME) {
      return !minValue;
    }
    if (item.name === QPL_MAXIMUM_UNIT_FIELD_NAME) {
      return !maxValue;
    }

    return false;
  }

//----------- Frequency field---------
  const onFrequencyChange = (newValue, oldValue) => {
    if (newValue && oldValue && newValue.value === oldValue.value ) {
      return;
    }
    const isPerMaterialQuantity = newValue?.value === getEnumValue(enumTypes.FREQUENCY_VALUE)('PER_MATERIAL_QUANTITY');
    change(name + '.[frequency]', isPerMaterialQuantity ? [{}] : null);

    onFieldChange?.(index, 'FREQUENCY', isPerMaterialQuantity);
  }

  const onFrequencyTypeChange = (newValue, oldValue, reduxName) => {
    if (newValue && oldValue && newValue.value === oldValue.value ) {
      return;
    }
    change(reduxName + '.[frequencyInterval]', null);
    setFrequencyUpdateKey(frequencyUpdateKey => 1 - frequencyUpdateKey);
  }

  const renderFrequencyField = (item, label, fieldIndex) => {
    return (
      <React.Fragment key={item.id + '-' + fieldIndex}>
        <InputSection label={label}  className="frequency-list-section">
          <ComboboxPortal id={name + '.[frequencySelect]'}
                          name={name + '.[frequencySelect]'}
                          options={frequencyOptions()}
                          onChangeCallback={onFrequencyChange}
                          validate={Validation.dropdown.required}
                          isDisabled={isViewOnly}
          />
        </InputSection>
        {!!isShowPerMaterialQuantity(testFields, index) &&
          <InputSection key={item.id + '-pv-' + fieldIndex} label={labels.get('frequencyPerMaterial') + '*'}  className="list-section">
            {renderFrequencyPerMaterialList()}
          </InputSection>}
  </React.Fragment>
    )
  }

  const getFrequencyPerMaterialItemRenderer = (data) => {
    const isInterval = (data?.fieldData?.frequencyType?.value === getEnumValue(enumTypes.FREQUENCY_VALUE)('INTERVAL_RAW_MATERIAL'));

    return (

      <RangeListItem
        {...data}
        unitType={majorUOMType}
        change={change}
        isViewOnly={isViewOnly}
        validate={[Validation.number.required, validateFrequencyRangeConflict]}
      >
        {({ name }) => (
          <>
            <ComboboxPortal
              id={name + '.[frequencyType]'}
              name={name + '.[frequencyType]'}
              options={frequencyTypesOptions()}
              className='frequency-type'
              placeholder={labels.get('frequencyType')}
              onChangeCallback={(a,b) => onFrequencyTypeChange(a, b, name)}
              validate={Validation.dropdown.required}
              isDisabled={isViewOnly}
            />
            {isInterval && <TextField
              id={name + '.[frequencyInterval]'}
              name={name + '.[frequencyInterval]'}
              className="short-textfield"
              placeholder={labels.get('interval')}
              normalize={Normalize.number(true, 1, 9999)}
              validate={Validation.required}
              disabled={isViewOnly}
            />}
          </>
        )}
      </RangeListItem>
    )
  };

  const renderFrequencyPerMaterialList = () => {
    return (
      <List
        updateKey={frequencyUpdateKey}
        id={name + '.[frequency]'}
        name={name + '.[frequency]'}
        className={cn('frequency-values-list values-list', { 'show-controls-on-hover': !isViewOnly})}
        itemRenderer={getFrequencyPerMaterialItemRenderer}
        minFieldsCount={1}
        addButtonLabel={labels.get('button.add')}
        isViewOnly={isViewOnly}
      />
    )
  }

  //----------- Attachments List field---------
  const getAttachmentItemRenderer = (data) => {
    return (<QplTestAttachmentListItem {...data} change={change} isViewOnly={isViewOnly}/>
    )
  };

  const renderAttachmentsField = (item, label, fieldIndex) => {
    return (
      <InputSection key={`${item.id}-${index}-${fieldIndex}`} label={label}
                    className="list-section custom-field"
                    labelExtraComponent={renderRemoveFieldButton(item)}
      >
        <List
          id={name + `.[${item.name}]`}
          name={name + `.[${item.name}]`}
          className={cn('attachments-list', { 'show-controls-on-hover': !isViewOnly})}
          itemRenderer={getAttachmentItemRenderer}
          minFieldsCount={1}
          maxItemsToRender={4}
          addButtonLabel={labels.get('button.add')}
          isViewOnly={isViewOnly}
        />

      </InputSection>
    )
  }

  const renderEmptyField = (item, label, fieldIndex) => {
    return (
      <InputSection key={`${item.id}-${index}-${fieldIndex}`} label={label}
                    className="custom-field"
                    labelExtraComponent={renderRemoveFieldButton(item)}
      />
    )
  }


  //----------- Pass Criteria field---------
  const renderPassCriteriaField = (item, label, fieldIndex) => {
    return (
      <InputSection key={item.id + '-' + fieldIndex} label={label} htmlFor={name + `.[${item.name}]`}>
        <ComboboxPortal
          id={name + `.[${item.name}]`}
          name={name + `.[${item.name}]`}
          options={qplTestPassCriteriaOptions()}
          validate={Validation.dropdown.required}
          isDisabled={isViewOnly}
        />

      </InputSection>
    )
  }

  //----------- common Test field---------
  const onDecimalFieldChange = (value, itemName) => {

    const val = value?.trim?.() || '';

    // Reject invalid values
    if (Number.isNaN(Number(val))) {
      return;
    }

    if (itemName === QPL_MINIMUM_VALUE_FIELD_NAME) {
      setMinValue(val || null);
      if (!val) {
        change(name + '.[minimumUnit]', '');
      }
      return;
    }
    if (itemName === QPL_MAXIMUM_VALUE_FIELD_NAME) {
      setMaxValue(val || null);
      if (!val) {
        change(name + '.[maximumUnit]', '');
      }
    }
  }

  const renderBooleanField = (item, label, fieldIndex) => {
    const fieldName = `${name}.[${item.name}]`
    return (
      <InputSection key={item.id + '-' + fieldIndex} label={label} htmlFor={fieldName}>
        <Checkbox id={fieldName} name={fieldName} label={''} disabled={isViewOnly}/>
      </InputSection>
    );
  }

  const renderDecimalField = (item, label, fieldIndex) => {
    const fieldName = `${name}.[${item.name}]`
    return (
      <InputSection key={item.id + '-' + fieldIndex} label={label} htmlFor={fieldName}>
        <TextField
          id={fieldName} name={fieldName}
          className='decimal-field'
          onChangeCallback={(value) => onDecimalFieldChange(value, item.name)}
          normalize={Normalize.normalizeFloat( QPL_MIN_DECIMAL, QPL_MAX_DECIMAL, 5)}
          validate={isMandatoryItem(item) ? [Validation.required, Validation.number.valid] : Validation.number.valid}
          disabled={isViewOnly}
        />
      </InputSection>
    );
  }

  const renderCommonField = (item, label, fieldIndex) => {

    const fieldName = `${name}.[${item.name}]`;

    return (
      <InputSection key={item.id + '-' + fieldIndex} label={label} htmlFor={fieldName} className={cn({'custom-field': isRemovable(item)})}
                    labelExtraComponent={renderRemoveFieldButton(item)}
      >
        <TextField
          id={fieldName} name={fieldName}
          className={cn({'short-textfield': !isRemovable(item) && fieldIndex !== 0}, {'wide-textfield': fieldIndex === 0})}
          maxLength={getMaxLength(item)}
          normalize={getNormalize(item)}
          validate={isMandatoryItem(item) && !isDisabledItem(item) ? Validation.required : undefined}
          disabled={isDisabledItem(item) || isViewOnly}
        />
      </InputSection>
    );
  }

  //----------- renderers and handlers---------
  const onMenuItemClick = (item) => {
    setShowOptionalFieldsMenu(false);
    onFieldAdd?.(item?.data, index);
  }

  const renderRemoveFieldButton = (item)  => {
    if (isRemovable(item) && !isViewOnly) {
      return (
        <span className="custom-field-remove-btn" onClick={(e) => onFieldRemoveHandler(item, e)}>
          <span className='pl pl-x-input-close'/>
        </span>);
    }
    return null;
  }

  const onFieldRemoveHandler = (item, e) => {
    e.stopPropagation();
    onFieldRemove?.(item, index);
  }

  const getAvailableOptionalFields = () => {
    const ids = testFields[index]?.map(item => item.id) ?? [];
    const hasAttachments = testFields[index]?.some(item => item.type === 'ATTACHMENT' || item.type === 'GENERIC_ATTACHMENT')
    return optionalFields?.filter((item) => {
        if (item.data.type === 'ATTACHMENT' || item.data.type === 'GENERIC_ATTACHMENT') {
          return !hasAttachments;
        }
        return !ids.includes(item.value);

    });
  }

  const renderAddFieldButton = () => {
    const availableOptionalFields = getAvailableOptionalFields();

    return (
      <div className="optional-fields-container">
        <Button id="add-field-btn"
                className="add-field-btn"
                onClick={() => setShowOptionalFieldsMenu(true)}
                disabled={optionalFields?.length - availableOptionalFields?.length >= 10 || isViewOnly}
        >
          <span className="pl pl-icon-add"/> <span>{labels.get('addField')}</span>
        </Button>

        <NoFormComboboxPortal
          id='optionalFieldsMenu'
          className='optional-fields-menu'
          tabIndex='-1'
          options={showOptionalFieldsMenu ? availableOptionalFields : []}
          menuIsOpen={showOptionalFieldsMenu}
          maxMenuHeight={210}
          onMenuOpen={() => setShowOptionalFieldsMenu(true)}
          onMenuClose={() => setShowOptionalFieldsMenu(false)}
          onChangeCallback={onMenuItemClick}
          onKeyDown={(e) => e.preventDefault()}  // prevent keyboard input
          components={{
            IndicatorsContainer: () => null,
          }}
        />

      </div>
    )
  }

  const renderTestFields = () => {
    return testFields?.[index]?.map((item, fieldIndex) => {

      const label = item.custom ? item.displayName : allLabels.get(item.labelKey) + (isMandatoryItem(item) ? ' *' : '');

      if (item.type === 'GENERIC_ATTACHMENT') {
        return renderEmptyField(item, label, fieldIndex);
      }
      if (item.type === 'ATTACHMENT') {
        return renderAttachmentsField(item, label, fieldIndex);
      }
      if (item.type === 'PASS_CRITERIA') {
        return renderPassCriteriaField(item, label, fieldIndex);
      }
      if ( item.type === 'FREQUENCY' ) {
        return renderFrequencyField(item, label, fieldIndex);
      }
      if ( item.type === 'BOOLEAN' ) {
        return renderBooleanField(item, label, fieldIndex);
      }
      if ( item.type === 'DECIMAL' ) {
        return renderDecimalField(item, label, fieldIndex);
      }
      return renderCommonField(item, label, fieldIndex);
    })
  }

  const renderSetType = () => {
    if (testType === getEnumValue(enumTypes.TEST_TYPE)('QUALITY')) {
      return <InputSection className={'qpl-test-type-label'} label={labels.get('setTypePassFail')}/>
    }
    return null;
  }

  return (
    <div className="qpl-test-list-item">
      {renderSetType()}
      {renderTestFields()}
      {renderAddFieldButton()}
    </div>
  );
};

QplTestListItem.propTypes = {
  name: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  listName: PropTypes.string.isRequired,
  testFields: PropTypes.array,
  optionalFields: PropTypes.array,
  change: PropTypes.func.isRequired,
  onFieldAdd: PropTypes.func,
  onFieldRemove: PropTypes.func,
  onFieldChange: PropTypes.func,
  fieldData: PropTypes.object,
  isViewOnly: PropTypes.bool,
  testType: PropTypes.string,
};


export default QplTestListItem;


const QplTestAttachmentListItem = ({ name, change, isMandatory=false,
                                     index,
                                     allFieldsData,
                                     isViewOnly=false,
                         }) => {

  const [isTypeSelected, setIsTypeSelected] = useState(false);

  const onAttachmentTypeChangeCallback = (value) => {
    if (!value) {
      change(name + '.[attachmentCount]', null);
    }
    setIsTypeSelected(!!value);
  }

  const typesInUse = allFieldsData?.map?.(item => item?.attachmentType?.value) ?? []
  const optionsAllowed = attachmentTypeOptions().filter( (item) => !typesInUse?.includes(item.value) || item.value === typesInUse?.[index])

  return (
    <div className="attachments-list-item">
      <ComboboxPortal
        id={name + '.[attachmentType]'}
        name={name + '.[attachmentType]'}
        options={optionsAllowed}
        onChangeCallback={onAttachmentTypeChangeCallback}
        validate={isMandatory ? Validation.dropdown.required : undefined}
        isDisabled={isViewOnly}
      />
      <TextField
        id={name + '.[attachmentCount]'}
        name={name + '.[attachmentCount]'}
        normalize={Normalize.number(true, 1, 99999)}
        validate={(isTypeSelected || isMandatory) ? Validation.required : undefined}
        disabled={isViewOnly}
      />
    </div>
  )
}
